import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  GripVertical,
  GripHorizontal,
  History,
  ChevronDown,
  PanelLeft,
  PanelRight,
  PanelBottom,
  Layout,
  HelpCircle,
  Pencil,
  Check,
  AlertCircle,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getDefaultPalette } from '../utils/colorPalettes';
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from 'react-resizable-panels';
import bbox from '@turf/bbox';
import { useLayout } from '../context/LayoutContext';

import PreviewTable from '../components/workspace/PreviewTable';
import ConnectModal from '../components/workspace/ConnectModal';
import BillingProjectModal from '../components/workspace/BillingProjectModal';
import ChatHistoryModal from '../components/workspace/ChatHistoryModal';
import DatasetExplorer from '../components/workspace/DatasetExplorer/DatasetExplorer';
import ChatPanel from '../components/workspace/Chat/ChatPanel';
import MapLayerList from '../components/workspace/Map/MapLayerList';
import MapLayerBuilderModal from '../components/workspace/Map/MapLayerBuilderModal';
import DataPanelTabs from '../components/workspace/Data/DataPanelTabs';
import MapPanel from '../components/workspace/Map/MapPanel';
import ConfirmModal from '../components/common/ConfirmModal';
import FunctionPreview from '../components/workspace/Data/FunctionPreview';
import FunctionLibraryModal from '../components/workspace/FunctionLibraryModal';
import AddItemModal from '../components/workspace/AddItemModal';
import GuidedTour from '../components/workspace/GuidedTour';

import { INITIAL_MESSAGES } from '../data/mockData';
import { workspaceApi } from '../api/workspaceApi';
import { chatApi } from '../api/chatApi';
import { mapApi } from '../api/mapApi';
import { dashboardApi } from '../api/dashboardApi';
import { h3ToGeoJSON, itemsToGeoJSON } from '../utils/mapUtils';
import { datasetApi } from '../api/datasetApi';
import { bigqueryApi } from '../api/bigqueryApi';
import { showInfoToast, showSuccessToast } from '../lib/toast';
import NotFoundPage from './NotFoundPage';

const HeaderUpdater = ({ workspace, workspaceId, bqStatus, showPanels, chatId, chatTitle, onGenerateTitle, handleNewChat, setIsHistoryModalOpen, togglePanel, onStartTour }) => {
  const { setHeaderContent } = useLayout();
  const navigate = useNavigate();
  const [systemLive, setSystemLive] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        // Use lightweight liveness endpoint for frequent checks
        const health = await dashboardApi.getHealth();
        setSystemLive(health.status === 'ok');
      } catch {
        setSystemLive(false);
      }
    };
    checkHealth();
    // Increased interval to 60s to reduce load - health checks are expensive
    const interval = setInterval(checkHealth, 60000); // Check every 60s
    return () => clearInterval(interval);
  }, []);

  // Load workspaces for dropdown
  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const data = await workspaceApi.getWorkspaces();
        setWorkspaces(data);
      } catch (err) {
        console.error('Failed to load workspaces:', err);
      }
    };
    loadWorkspaces();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsWorkspaceDropdownOpen(false);
      }
    };
    if (isWorkspaceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isWorkspaceDropdownOpen]);

  // Calculate dropdown position
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const handleToggleDropdown = useCallback((e) => {
    e.stopPropagation();
    if (!isWorkspaceDropdownOpen && buttonRef.current) {
      // Calculate position before opening
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      });
    }
    setIsWorkspaceDropdownOpen(!isWorkspaceDropdownOpen);
  }, [isWorkspaceDropdownOpen]);

  // Update position when workspace changes (in case layout shifts)
  useEffect(() => {
    if (isWorkspaceDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      });
    }
  }, [isWorkspaceDropdownOpen, workspace]);

  const handleWorkspaceSelect = useCallback((selectedWorkspaceId) => {
    if (selectedWorkspaceId !== workspaceId) {
      navigate(`/workspace/${selectedWorkspaceId}`);
    }
    setIsWorkspaceDropdownOpen(false);
  }, [navigate, workspaceId]);

  useEffect(() => {
    if (workspace && workspaceId) {
      setHeaderContent(
        <div className="w-full grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center h-full gap-4 min-w-0">
          {/* Left section: Workspace, Active Chat, History, New Chat */}
          <div className="flex items-center gap-1 min-w-0">
            <div className="relative shrink-0 mr-1">
              <button
                ref={buttonRef}
                type="button"
                data-tour="workspace-selector"
                onClick={handleToggleDropdown}
                className={cn(
                  "flex items-center gap-2 group rounded-xl px-2 py-1.5 transition-all border shrink-0",
                  isWorkspaceDropdownOpen
                    ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                    : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10"
                )}
              >
                <Layout
                  size={16}
                  className={cn(
                    "shrink-0 transition-colors",
                    isWorkspaceDropdownOpen
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-gray-500 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                  )}
                />
                <div className="flex flex-col shrink-0 min-w-0 text-left">
                  <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-none mb-0.5">Workspace</span>
                  <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight leading-none truncate max-w-[120px]">{workspace.name}</span>
                </div>
                <ChevronDown
                  size={14}
                  className={cn(
                    "shrink-0 transition-all",
                    isWorkspaceDropdownOpen
                      ? "text-emerald-600 dark:text-emerald-400 rotate-180"
                      : "text-gray-400 dark:text-gray-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                  )}
                />
              </button>

              {isWorkspaceDropdownOpen && workspaces.length > 0 && createPortal(
                <div
                  ref={dropdownRef}
                  className="fixed w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[100] max-h-96 overflow-y-auto backdrop-blur-sm"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`
                  }}
                >
                  <div className="p-2">
                    <div className="px-3 py-2 mb-1">
                      <div className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Switch Workspace</div>
                    </div>
                    <div className="space-y-0.5">
                      {workspaces.map((ws) => (
                        <button
                          key={ws.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWorkspaceSelect(ws.id);
                          }}
                          className={cn(
                            "w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all group",
                            ws.id === workspaceId
                              ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent"
                          )}
                        >
                          <Layout
                            size={16}
                            className={cn(
                              "shrink-0",
                              ws.id === workspaceId
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-gray-400 dark:text-gray-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400"
                            )}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-black uppercase tracking-tight truncate">{ws.name}</div>
                            {ws.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                {ws.description}
                              </div>
                            )}
                          </div>
                          {ws.id === workspaceId && (
                            <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>

            <div className="flex flex-col min-w-0 mr-1 px-1">
              <div className="flex items-center gap-1.5 shrink-0 mb-0.5">
                <div className={cn("w-1.5 h-1.5 rounded-full", systemLive ? "bg-emerald-500 animate-pulse" : "bg-red-500")} />
                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-wider leading-none whitespace-nowrap">
                  {systemLive ? 'Chat Active' : 'System Offline'}
                </span>
              </div>
              <div className="flex items-center gap-1 min-w-0">
                <span
                  className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-tight leading-none truncate"
                  title={chatTitle || 'Untitled Chat'}
                >
                  {chatTitle || 'Untitled Chat'}
                </span>
                {bqStatus?.reauth_required && workspace?.sources?.some(s => s.type === 'bigquery') && (
                  <div
                    className="flex items-center gap-1 ml-2 px-2 py-1 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg animate-in fade-in zoom-in duration-300"
                    title="BigQuery connection expired. Please reconnect in 'Data Sources'."
                  >
                    <AlertCircle size={10} className="text-red-600 dark:text-red-400" />
                    <span className="text-[8px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest leading-none">BQ Re-auth</span>
                  </div>
                )}
              </div>
            </div>

            <div data-tour="chat-nav" className="flex items-center gap-0.5 shrink-0">
              <button
                onClick={() => setIsHistoryModalOpen(true)}
                className="p-1.5 text-gray-400 hover:text-emerald-600 transition-colors shrink-0"
                title="History & Artefact Gallery"
              >
                <History size={18} />
              </button>
              <button
                onClick={handleNewChat}
                className="p-1.5 text-gray-400 hover:text-emerald-600 transition-colors shrink-0"
                title="New Chat"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Center section: View/Analysis Panel buttons */}
          <div className="flex items-center justify-center gap-2">
            <div data-tour="panel-toggles" className="flex items-center gap-1.5 bg-gray-50/50 dark:bg-gray-900/50 p-1 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm">
              <button
                onClick={() => togglePanel('chat')}
                className={cn(
                  "px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center gap-2",
                  showPanels.chat
                    ? "text-black dark:text-white bg-white dark:bg-gray-800 shadow-sm ring-1 ring-black/5 dark:ring-white/5"
                    : "text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white"
                )}
              >
                <PanelLeft size={14} /> Chat
              </button>
              <button
                onClick={() => togglePanel('data')}
                className={cn(
                  "px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center gap-2",
                  showPanels.data
                    ? "text-black dark:text-white bg-white dark:bg-gray-800 shadow-sm ring-1 ring-black/5 dark:ring-white/5"
                    : "text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white"
                )}
              >
                <PanelBottom size={14} /> Data
              </button>
              <button
                onClick={() => togglePanel('map')}
                className={cn(
                  "px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center gap-2",
                  showPanels.map
                    ? "text-black dark:text-white bg-white dark:bg-gray-800 shadow-sm ring-1 ring-black/5 dark:ring-white/5"
                    : "text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white"
                )}
              >
                <PanelRight size={14} /> Map
              </button>
            </div>
            <button
              onClick={onStartTour}
              className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900"
              title="Take a guided tour"
            >
              <HelpCircle size={16} />
            </button>
          </div>

          {/* Right section: balanced with spacer */}
          <div className="flex justify-end pointer-events-none">
            {/* Spacer to balance the logo on the left side of the header in AppLayout */}
            <div className="w-16" />
          </div>
        </div>
      );
    }
    return () => setHeaderContent(null);
  }, [workspace, workspaceId, bqStatus, showPanels, setHeaderContent, chatId, chatTitle, onGenerateTitle, handleNewChat, setIsHistoryModalOpen, togglePanel, systemLive, isWorkspaceDropdownOpen, workspaces, navigate, onStartTour, dropdownPosition.left, dropdownPosition.top, handleToggleDropdown, handleWorkspaceSelect]);

  return null;
};

const WorkspacePage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspace, setWorkspace] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [chatTitle, setChatTitle] = useState('Untitled Chat');
  const [messages, setMessages] = useState([]);
  const [previewDataset, setPreviewDataset] = useState(null);
  const [previewFunction, setPreviewFunction] = useState(null);
  const [previewMetadataVersion, setPreviewMetadataVersion] = useState(0);
  const [isFullScreenPreviewOpen, setIsFullScreenPreviewOpen] = useState(false);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isFunctionLibraryModalOpen, setIsFunctionLibraryModalOpen] = useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [addItemModalType, setAddItemModalType] = useState('dataset'); // 'dataset' | 'function'
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [shouldResumeImport, setShouldResumeImport] = useState(false);
  const [connectModalInitialStep, setConnectModalInitialStep] = useState(null);
  const [bqStatus, setBqStatus] = useState({ connected: false });
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isMapLayerBuilderModalOpen, setIsMapLayerBuilderModalOpen] = useState(false);
  const [isRemoveSourceModalOpen, setIsRemoveSourceModalOpen] = useState(false);
  const [isRemoveFunctionModalOpen, setIsRemoveFunctionModalOpen] = useState(false);
  const [targetSourceToRemove, setTargetSourceToRemove] = useState(null);
  const [targetFunctionToRemove, setTargetFunctionToRemove] = useState(null);
  const [targetMessageId, setTargetMessageId] = useState(null);
  const [mapLayers, setMapLayers] = useState([]); // Removed MOCK_MAP_LAYERS
  const [showPanels, setShowPanels] = useState({
    chat: true,
    map: true,
    data: true
  });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const mapRef = useRef(null);
  const hasAutoLoaded = useRef(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isInitializingChat, setIsInitializingChat] = useState(true);
  const abortControllerRef = useRef(null);
  const [notFound, setNotFound] = useState(false);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const preTourPanelsRef = useRef(null);
  const [isEditingWorkspaceMeta, setIsEditingWorkspaceMeta] = useState(false);
  const [isSavingWorkspaceMeta, setIsSavingWorkspaceMeta] = useState(false);
  const [editedWorkspaceName, setEditedWorkspaceName] = useState('');
  const [editedWorkspaceDescription, setEditedWorkspaceDescription] = useState('');

  // Helper function to persist chatId to URL
  const persistChatId = useCallback((chatIdToPersist) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (chatIdToPersist) {
      newSearchParams.set('chat', chatIdToPersist);
    } else {
      newSearchParams.delete('chat');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Reset auto-load tracker when switching workspaces
  useEffect(() => {
    hasAutoLoaded.current = false;
    setChatId(null);
    setChatTitle('Untitled Chat');
    setMessages([]);
    setIsInitializingChat(true);
    // Note: Don't clear persisted chatId here - let initWorkspace handle loading the appropriate chat
  }, [id]);

  const handleSelectChat = useCallback((chatIdToSelect, messages, title) => {
    setChatId(chatIdToSelect);
    setChatTitle(title || 'Untitled Chat');
    setIsInitializingChat(false);
    persistChatId(chatIdToSelect); // Persist to URL
    if (!messages || messages.length === 0) {
      setMessages(INITIAL_MESSAGES);
    } else {
      setMessages(messages.map(m => ({
        id: m.id,
        role: m.user_id === '00000000-0000-0000-0000-000000000000' ? 'assistant' : 'user',
        content: m.content,
        // Plot data
        sql: m.extra?.sql,
        chartData: m.extra?.rows,
        spec: m.extra?.visualizationSpec,
        visualization: m.extra?.visualizationSpec?.title,
        rowCount: m.extra?.rows ? m.extra.rows.length : (m.extra?.rowCount || 0),
        // Map data
        isMapResult: m.type === 'map' || !!m.extra?.layer_id || !!m.extra?.map_layer_id,
        layer_id: m.extra?.layer_id || m.extra?.map_layer_id || m.extra?.map_query?.layer_id,
        name: m.extra?.name || m.extra?.map_query?.name,
        // Base metadata
        type: m.type, // Preserve type to identify artifact messages
        extra: m.extra, // Preserve extra to check for artifact data
        isLoading: false
      })));
    }
  }, [persistChatId]);

  const handleGenerateTitle = useCallback(async (targetChatId) => {
    const cid = targetChatId || chatId;
    if (!cid) return;
    try {
      const data = await chatApi.generateTitle(cid);
      setChatTitle(data.title);
    } catch (err) {
      console.error('Failed to generate title:', err);
    }
  }, [chatId]);

  const handleSelectArtefact = useCallback(async (artefact) => {
    try {
      if (artefact.type === 'map') {
        const stableId = `art-${artefact.id}`;
        // Calling handleMapQuery ensures data is fetched and layer is correctly added/updated
        // Pass artefact.id as layerId (UUID) to load from database, and stableId in config for frontend ID
        await handleMapQuery(artefact.id, artefact.title || 'Saved Map Layer', { ...artefact.config, stableId });
        setIsHistoryModalOpen(false);
        return;
      }

      // 1. Fetch chat messages
      const chatMessages = await chatApi.getMessages(artefact.chat_id);

      // 2. Fetch chat info to get the title
      const chats = await chatApi.getChats(id);
      const currentChat = chats.find(c => c.id === artefact.chat_id);

      // 3. Update current chat and messages
      handleSelectChat(artefact.chat_id, chatMessages, currentChat?.title);

      // 4. Ensure chat panel is visible
      setShowPanels(prev => ({ ...prev, chat: true }));

      // 5. Set the target message ID for scrolling
      setTargetMessageId(artefact.id);

      // 6. Close the history modal (which contains the artefact gallery)
      setIsHistoryModalOpen(false);
    } catch (err) {
      console.error('Failed to select artefact:', err);
    }
  }, [id, handleSelectChat]);

  const handleNewChat = useCallback(() => {
    setChatId(null);
    setChatTitle('Untitled Chat');
    setIsInitializingChat(false);
    setMessages(INITIAL_MESSAGES);
    persistChatId(null); // Clear persisted chatId from URL
  }, [persistChatId]);

  const loadWorkspace = useCallback(async (silent = false) => {
    try {
      setNotFound(false);
      const data = await workspaceApi.getWorkspaceById(id);
      setWorkspace(data);

      const bq = await bigqueryApi.getStatus();
      setBqStatus(bq);
    } catch (err) {
      const status = err.response?.status;
      if (status >= 400 && status < 500) {
        setNotFound(true);
      } else if (status >= 500) {
        setNotFound(true);
      } else {
        console.error('Failed to load workspace:', err);
      }
    } finally {
    }
  }, [id]);

  useEffect(() => {
    const initWorkspace = async () => {
      setIsInitializingChat(true);
      await loadWorkspace();

      // Auto-load chat if no chatId selected and we haven't auto-loaded yet
      if (!chatId && !hasAutoLoaded.current) {
        hasAutoLoaded.current = true;
        try {
          // Check URL query param for chatId
          const urlChatId = searchParams.get('chat');

          if (urlChatId) {
            // Get all chats to validate the chat exists and belongs to this workspace
            const chats = await chatApi.getChats(id);
            const chatToLoad = chats.find(c => c.id === urlChatId);

            if (chatToLoad) {
              // Valid chat found in URL - load it
              const chatMessages = await chatApi.getMessages(chatToLoad.id);
              handleSelectChat(chatToLoad.id, chatMessages, chatToLoad.title);
            } else {
              // ChatId in URL is invalid - clear it and show new chat
              persistChatId(null);
              setMessages(INITIAL_MESSAGES);
              setIsInitializingChat(false);
            }
          } else {
            // No chatId in URL - show new chat
            setMessages(INITIAL_MESSAGES);
            setIsInitializingChat(false);
          }
        } catch (err) {
          console.error('Failed to load workspace chats:', err);
          // On error, show new chat
          setMessages(INITIAL_MESSAGES);
          setIsInitializingChat(false);
        }
      } else {
        setIsInitializingChat(false);
      }
    };
    initWorkspace();
  }, [id, chatId, handleSelectChat, loadWorkspace, searchParams, persistChatId]);

  useEffect(() => {
    if (!workspace) return;
    setEditedWorkspaceName(workspace.name || '');
    setEditedWorkspaceDescription(workspace.description || '');
  }, [workspace]);

  // Polling for workspace dataset updates
  useEffect(() => {
    if (!workspace?.sources) return;

    const hasPending = workspace.sources.some(s => s.status === 'loading' || s.status === 'generating');
    if (!hasPending) return;

    const interval = setInterval(() => {
      loadWorkspace(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [workspace?.sources, loadWorkspace]);


  useEffect(() => {
    if (workspace) {
      document.title = `manteo.ai | ${workspace.name}`;
    } else {
      document.title = 'manteo.ai | Workspace';
    }
  }, [workspace]);

  // Auto-start the guided tour on first visit
  useEffect(() => {
    if (workspace && !localStorage.getItem('manteo_tour_completed')) {
      // Small delay to let the workspace panels render
      const timer = setTimeout(() => {
        handleStartTour();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [workspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReorderLayers = useCallback((newOrder) => {
    setMapLayers(newOrder);
  }, []);

  const toggleLayerVisibility = (layerId) => {
    setMapLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, visible: !l.visible } : l
    ));
  };

  const updateLayerTheme = (layerId, color, palette = null, inverted = false) => {
    setMapLayers(prev => prev.map(l => {
      if (l.id === layerId) {
        const newConfig = { ...l.config };

        // Always use palette mode
        if (palette !== null) {
          newConfig.palette = palette;
          newConfig.inverted = inverted;
        } else {
          // Default to viridis if no palette provided
          newConfig.palette = getDefaultPalette();
          newConfig.inverted = inverted;
        }

        // Remove any old color config
        delete newConfig.color;

        return { ...l, config: newConfig };
      }
      return l;
    }));
  };

  const filterLayer = (layerId, valueRange) => {
    setMapLayers(prev => prev.map(l => {
      if (l.id === layerId) {
        // If clicking the same range again, clear the filter
        const currentFilter = l.valueFilter;
        if (currentFilter && currentFilter.min === valueRange.min && currentFilter.max === valueRange.max) {
          return { ...l, valueFilter: null };
        }
        return { ...l, valueFilter: valueRange };
      }
      return l;
    }));
  };

  const togglePanel = useCallback((panel) => {
    setShowPanels(prev => {
      const next = { ...prev, [panel]: !prev[panel] };
      if (!Object.values(next).some(Boolean)) return prev;
      return next;
    });
  }, []);

  const handleStartTour = useCallback(() => {
    // Save current panel state so we can restore it after the tour
    preTourPanelsRef.current = { ...showPanels };
    // Ensure all panels are visible so every tour step has a target
    setShowPanels({ chat: true, map: true, data: true });
    setIsTourRunning(true);
  }, [showPanels]);

  const handleFinishTour = useCallback(() => {
    setIsTourRunning(false);
    // Restore panels to their state before the tour started
    if (preTourPanelsRef.current) {
      setShowPanels(preTourPanelsRef.current);
      preTourPanelsRef.current = null;
    }
  }, []);

  const handleRemoveLayer = (layerId) => {
    setMapLayers(prev => prev.filter(l => l.id !== layerId));
  };

  const handleFlyToLayer = (layerId) => {
    const layer = mapLayers.find(l => l.id === layerId);
    if (!layer || !layer.data || !layer.data.features || layer.data.features.length === 0) {
      return;
    }

    // Use filtered data if filter is active, otherwise use full data
    let dataToUse = layer.data;
    if (layer.valueFilter && layer.data && layer.data.features) {
      dataToUse = {
        ...layer.data,
        features: layer.data.features.filter(feature => {
          const value = feature.properties?.value;
          if (value === null || value === undefined) return false;
          // For non-last bands, use exclusive upper bound (<) to avoid overlaps
          // For last band, use inclusive upper bound (<=)
          if (layer.valueFilter.isLast) {
            return value >= layer.valueFilter.min && value <= layer.valueFilter.max;
          } else {
            return value >= layer.valueFilter.min && value < layer.valueFilter.max;
          }
        })
      };
    }

    if (dataToUse.features.length > 0 && mapRef.current) {
      const [minLng, minLat, maxLng, maxLat] = bbox(dataToUse);
      mapRef.current.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: 100, duration: 1500 }
      );
    }
  };

  const handleMapQuery = async (layerIdOrSpec, name, config = {}) => {
    const { assistantMessageId, sql, stableId } = config;

    // Determine if first parameter is a layerId (UUID string) or a query spec (object)
    const isLayerId = typeof layerIdOrSpec === 'string' && layerIdOrSpec.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    const layerId = isLayerId ? layerIdOrSpec : null;
    const querySpec = isLayerId ? null : layerIdOrSpec;

    const newId = stableId || layerId || `map-${Date.now()}`;

    // Immediately add layer with loading state
    const loadingLayer = {
      id: newId,
      name: name || 'AI-Map Layer',
      visible: true,
      hue: Math.floor(Math.random() * 360),
      sat: 72,
      dataset: null,  // Will be loaded from database or spec
      data: null,
      stats: null,
      palette: config.palette || 'emerald',
      config: config,
      layer_id: layerId,  // Store layer_id for reference (null if using spec)
      status: 'loading',
      progress: 'Fetching data...',
      progressPercent: 0
    };

    setMapLayers(prev => {
      // If updating an existing layer (like preview), replace it
      if (prev.some(l => l.id === newId)) {
        return prev.map(l => l.id === newId ? loadingLayer : l);
      }
      return [loadingLayer, ...prev];
    });
    setShowPanels(prev => ({ ...prev, map: true }));

    try {
      // Update progress - Fetching data (0-30%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progress: 'Fetching data...', progressPercent: 10 } : l
      ));

      await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for smooth animation

      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progressPercent: 30 } : l
      ));

      // Query with layer_id if available, otherwise use query spec
      const queryPayload = layerId
        ? { layer_id: layerId }
        : querySpec;
      const data = await mapApi.queryMapData(queryPayload);

      // Update progress - Processing spatial data (30-70%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progress: 'Processing spatial data...', progressPercent: 50 } : l
      ));

      await new Promise(resolve => setTimeout(resolve, 100));

      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progressPercent: 70 } : l
      ));

      // Update progress - Rendering map (70-90%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progress: 'Rendering map...', progressPercent: 85 } : l
      ));

      // Detect geometry type and use appropriate conversion
      let geojson;
      if (data.items.length === 0) {
        // Handle empty results gracefully
        geojson = { type: 'FeatureCollection', features: [] };
      } else if (data.items[0].geometry) {
        geojson = itemsToGeoJSON(data.items);
      } else if (data.items[0].h3) {
        geojson = h3ToGeoJSON(data.items);
      } else {
        throw new Error('Unknown geometry type in map data');
      }

      // Update progress - Complete (100%)
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? { ...l, progressPercent: 100 } : l
      ));

      await new Promise(resolve => setTimeout(resolve, 200)); // Brief delay to show 100%

      const newLayer = {
        id: newId,
        name: name || 'AI-Map Layer',
        visible: true,
        hue: Math.floor(Math.random() * 360),
        sat: 72,
        dataset: null,  // Loaded from database via layer_id
        data: geojson,
        stats: data.stats,
        palette: config.palette || 'emerald',
        config: config,
        layer_id: layerId,  // Store layer_id for reference
        status: 'loaded',
        progress: null,
        progressPercent: null
      };

      setMapLayers(prev => {
        return prev.map(l => l.id === newId ? newLayer : l);
      });

      // Update chat message if assistantMessageId provided
      if (assistantMessageId) {
        setMessages(prev => {
          const newMessages = [...prev];
          const msgIdx = newMessages.findLastIndex(m => m.id === assistantMessageId);
          if (msgIdx !== -1) {
            const msg = { ...newMessages[msgIdx] };
            msg.sql = sql;
            msg.chartData = data.items.slice(0, 100); // Sample data for display
            msg.rowCount = data.items.length;
            msg.isMapResult = true;
            newMessages[msgIdx] = msg;
          }
          return newMessages;
        });
      }

      // Auto-zoom to fit the new layer data
      if (geojson.features.length > 0 && mapRef.current) {
        // Small delay to ensure map is ready
        setTimeout(() => {
          if (mapRef.current) {
            const [minLng, minLat, maxLng, maxLat] = bbox(geojson);
            mapRef.current.fitBounds(
              [[minLng, minLat], [maxLng, maxLat]],
              { padding: 100, duration: 1500 }
            );
          }
        }, 100);
      }
    } catch (error) {
      console.error('Failed to load map data:', error);
      // Update layer with error state
      setMapLayers(prev => prev.map(l =>
        l.id === newId ? {
          ...l,
          status: 'error',
          progress: 'Failed to load data',
          progressPercent: null,
          error: error.message || 'Unknown error'
        } : l
      ));
    }
  };

  const handlePreviewLayer = async (query, description, config, layerId = null) => {
    await handleMapQuery(layerId, description, { ...config, stableId: 'preview-layer' });
  };

  const handleViewMapLayer = useCallback(async (layerId) => {
    // Find the layer in mapLayers by layer_id
    const layer = mapLayers.find(l => l.layer_id === layerId);
    if (layer) {
      // Layer is already on the map, just ensure map panel is visible
      setShowPanels(prev => ({ ...prev, map: true }));
      // Optionally fly to the layer bounds if it has data
      if (layer.data && mapRef.current && layer.data.features?.length > 0) {
        try {
          const bboxFn = (await import('@turf/bbox')).default;
          const [minLng, minLat, maxLng, maxLat] = bboxFn(layer.data);
          mapRef.current.fitBounds(
            [[minLng, minLat], [maxLng, maxLat]],
            { padding: 100, duration: 1500 }
          );
        } catch (err) {
          console.warn('Failed to fit bounds:', err);
        }
      }
    } else {
      // Layer not found, try to load it from the database
      // Find the message to get the layer name
      const message = messages.find(m => m.layer_id === layerId);
      if (message) {
        await handleMapQuery(layerId, message.name || 'Map Layer', {});
        setShowPanels(prev => ({ ...prev, map: true }));
      } else {
        console.warn('Layer not found on map and no message found:', layerId);
      }
    }
  }, [mapLayers, messages, mapRef]);

  const handleGenerateMapLayer = useCallback(async (originalQuery) => {
    if (!originalQuery || !originalQuery.trim()) return;
    if (!chatId) return;

    // Find the message that triggered this and mark it as generating and requested
    setMessages(prev => prev.map(msg => {
      if (msg.originalQuery === originalQuery && msg.mappable) {
        return { ...msg, isGeneratingMap: true, mapLayerRequested: true };
      }
      return msg;
    }));

    const datasetIds = workspace.sources.map(s => s.id);
    const assistantMessageId = Date.now();

    // Add a new assistant message for the map generation
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: 'Generating map layer...',
      isLoading: true,
      agentSteps: [],
      isMapResult: true // Mark as map result early to prevent UI flash
    }]);

    try {
      await chatApi.generateMapLayer(chatId, originalQuery, datasetIds, 'openai', (event) => {
        // Handle side effects outside of the functional state update
        if (event.type === 'map_query') {
          handleMapQuery(event.layer_id, event.name, { assistantMessageId, sql: event.sql });
        }

        setMessages(prev => {
          const newMessages = [...prev];
          const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
          if (lastMsgIndex === -1) return prev;

          const lastMsg = { ...newMessages[lastMsgIndex] };

          if (event.type === 'summary') {
            lastMsg.content = (lastMsg.content ? lastMsg.content + '\n\n' : '') + event.content;
          } else if (event.type === 'agent_step') {
            const currentSteps = lastMsg.agentSteps || [];
            const stepExists = currentSteps.some(
              s => s.step === event.step && s.timestamp === (event.ts || Date.now())
            );
            if (!stepExists) {
              lastMsg.agentSteps = [
                ...currentSteps,
                {
                  step: event.step,
                  agent: event.agent,
                  timestamp: event.ts || Date.now()
                }
              ];
            }
          } else if (event.type === 'map_query') {
            lastMsg.content = event.description || 'Map layer generated';
            lastMsg.layer_id = event.layer_id;
            lastMsg.name = event.name;
            lastMsg.sql = event.sql;
            lastMsg.isMapResult = true;
            lastMsg.isLoading = false;
          } else if (event.type === 'error') {
            lastMsg.content = (lastMsg.content || '') + '\n' + (event.content || 'An error occurred.');
            lastMsg.isLoading = false;
          }

          newMessages[lastMsgIndex] = lastMsg;
          return newMessages;
        });
      });

      // Ensure loading is set to false when stream completes
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            isLoading: false
          };
        }
        // Also clear isGeneratingMap from the original message
        return newMessages.map(msg => {
          if (msg.originalQuery === originalQuery && msg.mappable) {
            return { ...msg, isGeneratingMap: false };
          }
          return msg;
        });
      });
    } catch (error) {
      console.error('Map generation error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            content: 'Sorry, I encountered an error generating the map layer. Please try again.',
            isLoading: false
          };
        }
        // Clear isGeneratingMap from the original message
        return newMessages.map(msg => {
          if (msg.originalQuery === originalQuery && msg.mappable) {
            return { ...msg, isGeneratingMap: false };
          }
          return msg;
        });
      });
    }
  }, [chatId, workspace?.sources]);

  const handleSend = useCallback(async (text) => {
    const messageText = text;
    if (!messageText || !messageText.trim()) return;

    // Prevent sending if chat is already loading
    if (isChatLoading) return;

    setMessages(prev => [...prev, { role: 'user', content: messageText }]);

    // Add placeholder assistant message
    const assistantMessageId = Date.now();
    setMessages(prev => [...prev, {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      isLoading: true
    }]);

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setIsChatLoading(true);

    try {
      // Ensure the new chat is associated with the current workspace ID
      const isNewChat = !chatId;
      const currentChatId = chatId || (await chatApi.createChat(id)).id;
      if (isNewChat) {
        setChatId(currentChatId);
        setChatTitle('Untitled Chat');
        persistChatId(currentChatId); // Persist new chat to URL
      }

      const datasetIds = workspace.sources.map(s => s.id);

      await chatApi.streamChat(currentChatId, messageText, datasetIds, 'openai', (event) => {
        // Handle side effects outside of the functional state update
        if (event.type === 'map_query') {
          handleMapQuery(event.layer_id, event.name, { assistantMessageId, sql: event.sql });
        }

        setMessages(prev => {
          const newMessages = [...prev];
          const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
          if (lastMsgIndex === -1) return prev;

          const lastMsg = { ...newMessages[lastMsgIndex] };

          if (event.type === 'summary') {
            lastMsg.content = (lastMsg.content ? lastMsg.content + '\n\n' : '') + event.content;
            // Keep loading until we get a result
          } else if (event.type === 'sql') {
            lastMsg.sql = event.content;
            // Store mappable flag if present
            if (event.mappable !== undefined) {
              lastMsg.mappable = event.mappable;
            }
            // Early detection of map result to prevent UI flash
            if (event.is_map_result) {
              lastMsg.isMapResult = true;
            }
            // Store original query for map generation
            if (!lastMsg.originalQuery) {
              lastMsg.originalQuery = messageText;
            }
            // Keep loading until we get a result
          } else if (event.type === 'agent_step') {
            // Initialize agentSteps array if it doesn't exist
            const currentSteps = lastMsg.agentSteps || [];
            // Check if this step already exists (by step text and timestamp)
            const stepExists = currentSteps.some(
              s => s.step === event.step && s.timestamp === (event.ts || Date.now())
            );
            // Only add if it doesn't exist
            if (!stepExists) {
              lastMsg.agentSteps = [
                ...currentSteps,
                {
                  step: event.step,
                  agent: event.agent,
                  timestamp: event.ts || Date.now()
                }
              ];
            }
            // Extract row count from agent_step message if it contains "returned X rows"
            const rowCountMatch = event.step?.match(/returned\s+(\d+)\s+rows?/i);
            if (rowCountMatch) {
              lastMsg.rowCount = parseInt(rowCountMatch[1], 10);
            }
            // Keep loading while agent is working
          } else if (event.type === 'map_query') {
            lastMsg.content = event.description || 'Map layer generated';
            lastMsg.layer_id = event.layer_id;
            lastMsg.name = event.name;
            lastMsg.sql = event.sql;
            lastMsg.isMapResult = true;
            lastMsg.isLoading = false; // Map query is a final result
            // Keep agent steps, just minimize them
          } else if (event.type === 'result') {
            // Updated to use the real structure from the backend
            if (event.visualizationSpec) {
              lastMsg.chartData = event.rows;
              lastMsg.spec = event.visualizationSpec;
              lastMsg.visualization = event.visualizationSpec.title || 'Dynamic Result';
              // Set row count from actual data if not already set
              if (lastMsg.rowCount === undefined && event.rows) {
                lastMsg.rowCount = event.rows.length;
              }
            } else if (event.content) {
              lastMsg.content = (lastMsg.content || '') + '\n' + event.content;
            }

            lastMsg.isLoading = false; // Result means we're done
            // Keep agent steps, just minimize them
          } else if (event.type === 'error') {
            lastMsg.content = (lastMsg.content || '') + '\n' + (event.content || 'An error occurred.');
            lastMsg.isLoading = false; // Error means we're done
            // Keep agent steps, just minimize them
          } else if (event.type === 'more_questions') {
            lastMsg.suggestions = event.questions || [];
          }

          newMessages[lastMsgIndex] = lastMsg;
          return newMessages;
        });
      }, abortController.signal);

      // Ensure loading is set to false when stream completes
      // Agent steps are kept and will be minimized
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            isLoading: false
          };
        }
        return newMessages;
      });

      // After first message in a new chat, auto-generate title
      if (isNewChat || (messages.length === 1 && messages[0].role === 'assistant')) {
        handleGenerateTitle(currentChatId);
      }
    } catch (error) {
      // Don't show error if it was aborted
      if (error.name === 'AbortError') {
        // Stream was stopped, don't show error
        return;
      }
      console.error('Chat error:', error);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsgIndex = newMessages.findLastIndex(m => m.id === assistantMessageId);
        if (lastMsgIndex !== -1) {
          newMessages[lastMsgIndex] = {
            ...newMessages[lastMsgIndex],
            content: 'Sorry, I encountered an error. Please try again.',
            isLoading: false
          };
        }
        return newMessages;
      });
    } finally {
      setIsChatLoading(false);
      abortControllerRef.current = null;
    }
  }, [id, chatId, workspace?.sources, isChatLoading, messages, handleGenerateTitle, persistChatId]);

  const handleStopChat = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsChatLoading(false);

    // Add stop message
    setMessages(prev => {
      const newMessages = [...prev];
      // Find the last assistant message that's loading
      const lastLoadingIndex = newMessages.findLastIndex(m => m.role === 'assistant' && m.isLoading);
      if (lastLoadingIndex !== -1) {
        // Mark it as not loading
        newMessages[lastLoadingIndex] = {
          ...newMessages[lastLoadingIndex],
          isLoading: false
        };
      }

      // Add stop message
      return [...newMessages, {
        id: Date.now(),
        role: 'assistant',
        content: '*Chat stopped by user. Even though the chat was stopped, the results may still be loading and will be available in the chat history.*',
        isLoading: false
      }];
    });
  }, []);

  const handleScrollComplete = useCallback(() => setTargetMessageId(null), []);

  const handleRemoveSource = (sourceId) => {
    setTargetSourceToRemove(sourceId);
    setIsRemoveSourceModalOpen(true);
  };

  const confirmRemoveSource = async () => {
    if (!targetSourceToRemove) return;
    try {
      const updatedSources = workspace.sources.filter(s => s.id !== targetSourceToRemove);
      const updatedWs = await workspaceApi.updateWorkspace(id, {
        name: workspace.name,
        description: workspace.description,
        sources: updatedSources.map(s => s.id)
      });
      setWorkspace(updatedWs);
      if (previewDataset?.id === targetSourceToRemove) {
        setPreviewDataset(null);
      }
      showSuccessToast('Dataset removed from workspace.');
    } catch (err) {
      console.error('Failed to remove source:', err);
    } finally {
      setIsRemoveSourceModalOpen(false);
      setTargetSourceToRemove(null);
    }
  };

  const confirmRemoveFunction = async () => {
    if (!targetFunctionToRemove) return;
    try {
      const updatedUdfs = workspace.udfs.filter(u => u.id !== targetFunctionToRemove);
      const updatedWs = await workspaceApi.updateWorkspace(id, {
        name: workspace.name,
        description: workspace.description,
        udf_ids: updatedUdfs.map(u => u.id)
      });
      setWorkspace(updatedWs);
      if (previewFunction?.id === targetFunctionToRemove) {
        setPreviewFunction(null);
      }
      showSuccessToast('Function removed from workspace.');
    } catch (err) {
      console.error('Failed to remove function:', err);
    } finally {
      setIsRemoveFunctionModalOpen(false);
      setTargetFunctionToRemove(null);
    }
  };

  const handleConnect = async (method, data) => {
    try {
      if (method === 'bigquery') {
        // 1. Import tables into TableMetadata first
        await datasetApi.importBigQueryTables(data);

        // 2. Now associate them with the workspace
        const newTableIds = data.tables.map(tbl => tbl.id);
        const currentTableIds = workspace.sources.map(s => s.id);

        // Remove duplicates
        const combinedTableIds = Array.from(new Set([...newTableIds, ...currentTableIds]));

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          sources: combinedTableIds
        });

        setWorkspace(updatedWs);
        showInfoToast('Tables imported and linked to this workspace.');
      } else if (method === 'upload') {
        const currentTableIds = workspace.sources.map(s => s.id);
        const combinedTableIds = Array.from(new Set([data.table_id, ...currentTableIds]));

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          sources: combinedTableIds
        });

        setWorkspace(updatedWs);
        showSuccessToast('Dataset added to workspace.');
      } else if (method === 'library') {
        // Handle both single dataset and array of datasets
        const datasetsToAdd = Array.isArray(data) ? data : [data];
        const newTableIds = datasetsToAdd.map(d => d.id);
        const currentTableIds = workspace.sources.map(s => s.id);
        const combinedTableIds = Array.from(new Set([...newTableIds, ...currentTableIds]));

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          sources: combinedTableIds
        });

        setWorkspace(updatedWs);
        const count = datasetsToAdd.length;
        showSuccessToast(count === 1 ? 'Dataset added to workspace.' : `${count} datasets added to workspace.`);
      } else if (method === 'udf-library') {
        // Handle both single UDF and array of UDFs
        const udfsToAdd = Array.isArray(data) ? data : [data];
        const newUdfIds = udfsToAdd.map(u => u.id);
        const currentUdfIds = workspace.udfs.map(u => u.id);
        const combinedUdfIds = Array.from(new Set([...newUdfIds, ...currentUdfIds]));

        const updatedWs = await workspaceApi.updateWorkspace(id, {
          name: workspace.name,
          description: workspace.description,
          udf_ids: combinedUdfIds
        });

        setWorkspace(updatedWs);
        // Auto-select the first newly added function if only one was added
        if (udfsToAdd.length === 1) {
          const addedUdf = updatedWs.udfs?.find(u => u.id === udfsToAdd[0].id);
          if (addedUdf) {
            setPreviewFunction(addedUdf);
            setPreviewDataset(null);
          }
        }
        showSuccessToast('Function library updated.');
      }
    } catch (err) {
      console.error(`Failed to connect dataset via ${method}:`, err);
    }
  };


  const handleRebuildSource = async (sourceId) => {
    try {
      await datasetApi.rebuildMetadata(sourceId);
      // Refresh workspace data to show processing status
      const data = await workspaceApi.getWorkspaceById(id);
      setWorkspace(data);
      showInfoToast('Metadata refresh started.');
    } catch (err) {
      console.error('Failed to rebuild metadata:', err);
    }
  };

  const handleSaveWorkspaceMeta = async () => {
    const trimmedName = editedWorkspaceName.trim();
    if (!trimmedName || isSavingWorkspaceMeta) return;

    setIsSavingWorkspaceMeta(true);
    try {
      const updatedWs = await workspaceApi.updateWorkspace(id, {
        name: trimmedName,
        description: editedWorkspaceDescription.trim()
      });
      setWorkspace(updatedWs);
      setIsEditingWorkspaceMeta(false);
      showSuccessToast('Workspace details saved.');
    } catch (err) {
      console.error('Failed to update workspace details:', err);
    } finally {
      setIsSavingWorkspaceMeta(false);
    }
  };

  const chatWorkspaceHeader = workspace ? (
    <div className="mb-4 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-white via-white to-emerald-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-emerald-950/20 p-4 shadow-sm backdrop-blur-sm">
      {!isEditingWorkspaceMeta ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-black text-emerald-600/80 dark:text-emerald-400/80 uppercase tracking-[0.18em] mb-1">Workspace</p>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight truncate">
              {workspace?.name || 'Untitled Workspace'}
            </h2>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1.5 leading-relaxed line-clamp-2">
              {workspace?.description || 'No description provided.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsEditingWorkspaceMeta(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-emerald-50/60 dark:hover:bg-emerald-900/20 transition-all"
          >
            <Pencil size={12} />
            Edit
          </button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div>
            <label className="text-[9px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest ml-1">Workspace Name</label>
            <input
              className="mt-1 w-full bg-white dark:bg-gray-900 border-2 border-transparent focus:border-emerald-500 rounded-xl px-3 py-2.5 text-gray-900 dark:text-white font-bold text-sm transition-all outline-none"
              value={editedWorkspaceName}
              onChange={(e) => setEditedWorkspaceName(e.target.value)}
              placeholder="Workspace name"
            />
          </div>
          <div>
            <label className="text-[9px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest ml-1">Description (Optional)</label>
            <textarea
              rows={2}
              className="mt-1 w-full bg-white dark:bg-gray-900 border-2 border-transparent focus:border-emerald-500 rounded-xl px-3 py-2.5 text-gray-900 dark:text-white font-medium text-sm transition-all outline-none resize-none"
              value={editedWorkspaceDescription}
              onChange={(e) => setEditedWorkspaceDescription(e.target.value)}
              placeholder="Describe what this workspace is for"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditedWorkspaceName(workspace?.name || '');
                setEditedWorkspaceDescription(workspace?.description || '');
                setIsEditingWorkspaceMeta(false);
              }}
              disabled={isSavingWorkspaceMeta}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 transition-all disabled:opacity-50"
            >
              <X size={12} />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveWorkspaceMeta}
              disabled={isSavingWorkspaceMeta || !editedWorkspaceName.trim()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-all disabled:opacity-50"
            >
              <Check size={12} />
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const onHover = (event) => {
    const {
      features,
      point: { x, y }
    } = event;
    const hoveredFeature = features && features[0];

    if (hoveredFeature) {
      setHoverInfo({
        feature: hoveredFeature,
        x,
        y,
        lngLat: event.lngLat
      });
    } else {
      setHoverInfo(null);
    }
  };

  const onMapClick = (event) => {
    const {
      features,
      point: { x, y }
    } = event;
    const clickedFeature = features && features[0];

    if (clickedFeature) {
      setSelectedInfo({
        feature: clickedFeature,
        x,
        y,
        lngLat: event.lngLat
      });
    } else {
      setSelectedInfo(null);
    }
  };


  if (notFound) return <NotFoundPage />;
  if (!workspace) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950 overflow-hidden transition-colors duration-300">
      <HeaderUpdater
        workspace={workspace}
        workspaceId={id}
        bqStatus={bqStatus}
        showPanels={showPanels}
        chatId={chatId}
        chatTitle={chatTitle}
        onGenerateTitle={() => handleGenerateTitle()}
        handleNewChat={handleNewChat}
        setIsHistoryModalOpen={setIsHistoryModalOpen}
        togglePanel={togglePanel}
        onStartTour={handleStartTour}
      />
      <GuidedTour run={isTourRunning} onFinish={handleFinishTour} />
      {isFullScreenPreviewOpen && (
        <PreviewTable
          dataset={previewDataset}
          onClose={() => setIsFullScreenPreviewOpen(false)}
          onMetadataUpdate={() => setPreviewMetadataVersion(v => v + 1)}
        />
      )}
      <ConnectModal
        isOpen={isConnectModalOpen}
        mode="workspace"
        onClose={() => {
          setIsConnectModalOpen(false);
          setConnectModalInitialStep(null);
          setShouldResumeImport(false);
        }}
        onConnect={handleConnect}
        initialStep={connectModalInitialStep || (shouldResumeImport ? 'bigquery-dataset' : null)}
        onBillingRequired={() => {
          setIsConnectModalOpen(false);
          setShouldResumeImport(true);
          setIsBillingModalOpen(true);
        }}
        currentSources={workspace?.sources || []}
      />

      <FunctionLibraryModal
        isOpen={isFunctionLibraryModalOpen}
        onClose={() => setIsFunctionLibraryModalOpen(false)}
        onSelect={(udfs) => {
          handleConnect('udf-library', udfs);
          setIsFunctionLibraryModalOpen(false);
        }}
        currentUdfIds={workspace?.udfs?.map(u => u.id) || []}
      />
      <AddItemModal
        isOpen={isAddItemModalOpen}
        type={addItemModalType}
        onClose={() => setIsAddItemModalOpen(false)}
        onSelect={async (method, data) => {
          await handleConnect(method, data);
        }}
        currentItemIds={
          addItemModalType === 'dataset'
            ? workspace?.sources?.map(s => s.id) || []
            : workspace?.udfs?.map(u => u.id) || []
        }
      />
      <BillingProjectModal
        isOpen={isBillingModalOpen}
        onClose={() => {
          setIsBillingModalOpen(false);
          setShouldResumeImport(false);
        }}
        onProjectSelected={async () => {
          const bq = await bigqueryApi.getStatus();
          setBqStatus(bq);
          if (shouldResumeImport) {
            setShouldResumeImport(false);
            setIsConnectModalOpen(true);
          }
        }}
        currentBillingProject={bqStatus.billing_project_id}
      />
      <ChatHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        onSelectChat={handleSelectChat}
        onSelectArtefact={handleSelectArtefact}
        workspaceId={id}
        workspaceName={workspace?.name}
      />

      <MapLayerBuilderModal
        isOpen={isMapLayerBuilderModalOpen}
        onClose={() => {
          setIsMapLayerBuilderModalOpen(false);
          // Clean up any temporary preview ghost when closing
          handleRemoveLayer('preview-layer');
        }}
        workspaceId={id}
        chatId={chatId}
        availableDatasets={workspace?.sources || []}
        onSave={(payload, desc, config, layerId) => {
          // Clean up any ghost preview before adding the permanent layer
          handleRemoveLayer('preview-layer');
          // If layerId exists, use it; otherwise use the query spec (payload)
          const stableId = layerId ? `art-${layerId}` : undefined;
          handleMapQuery(layerId || payload, desc, { ...config, stableId });
        }}
        onPreview={handlePreviewLayer}
      />

      <ConfirmModal
        isOpen={isRemoveSourceModalOpen}
        onClose={() => setIsRemoveSourceModalOpen(false)}
        onConfirm={confirmRemoveSource}
        title="Remove Dataset"
        description="Are you sure you want to remove this dataset from this workspace? This won't delete the data from the library."
        confirmText="Remove"
        variant="danger"
      />

      <ConfirmModal
        isOpen={isRemoveFunctionModalOpen}
        onClose={() => setIsRemoveFunctionModalOpen(false)}
        onConfirm={confirmRemoveFunction}
        title="Remove Function"
        description="Are you sure you want to remove this function from this workspace?"
        confirmText="Remove"
        variant="danger"
      />

      <div className="flex-1 overflow-hidden">
        <PanelGroup orientation="vertical" className="h-full w-full">
          {(showPanels.chat || showPanels.map) && (
            <Panel defaultSize={showPanels.data ? 70 : 100} minSize={25}>
              <PanelGroup orientation="horizontal" className="h-full w-full">
                {showPanels.chat && (
                  <Panel minSize={25} defaultSize={showPanels.map ? 50 : 100}>
                    <div data-tour="chat-panel" className="h-full">
                      <ChatPanel
                        messages={messages}
                        chatHeader={chatWorkspaceHeader}
                        onSend={handleSend}
                        targetMessageId={targetMessageId}
                        onScrollComplete={handleScrollComplete}
                        isChatLoading={isChatLoading}
                        isInitializingChat={isInitializingChat}
                        onStopChat={handleStopChat}
                        onNewChat={handleNewChat}
                        onAddLayer={handleGenerateMapLayer}
                        onViewMapLayer={handleViewMapLayer}
                      />
                    </div>
                  </Panel>
                )}
                {showPanels.chat && showPanels.map && (
                  <Separator orientation="horizontal" />
                )}
                {showPanels.map && (
                  <Panel minSize={25} defaultSize={showPanels.chat ? 50 : 100}>
                    <div data-tour="map-panel" className="h-full w-full bg-white dark:bg-gray-950 relative">
                      <MapPanel
                        mapRef={mapRef}
                        mapLayers={mapLayers}
                        hoverInfo={hoverInfo}
                        selectedInfo={selectedInfo}
                        onMouseMove={onHover}
                        onMapClick={onMapClick}
                        onToggleLayerVisibility={toggleLayerVisibility}
                        onRemoveLayer={handleRemoveLayer}
                        onUpdateLayerTheme={updateLayerTheme}
                        onOpenBuilder={() => setIsMapLayerBuilderModalOpen(true)}
                      />

                      <MapLayerList
                        layers={mapLayers}
                        workspaceId={id}
                        chatId={chatId}
                        availableDatasets={workspace?.sources || []}
                        onToggleVisibility={toggleLayerVisibility}
                        onRemoveLayer={handleRemoveLayer}
                        onUpdateTheme={updateLayerTheme}
                        onAddLayer={handleMapQuery}
                        onPreviewLayer={handlePreviewLayer}
                        onOpenBuilder={() => setIsMapLayerBuilderModalOpen(true)}
                        onFilterLayer={filterLayer}
                        onFlyToLayer={handleFlyToLayer}
                        onReorderLayers={handleReorderLayers}
                      />
                    </div>
                  </Panel>
                )}
              </PanelGroup>
            </Panel>
          )}
          {(showPanels.chat || showPanels.map) && showPanels.data && <Separator orientation="vertical" />}
          {showPanels.data && (
            <Panel minSize={25} defaultSize={(showPanels.chat || showPanels.map) ? 30 : 100}>
              <div data-tour="data-panel" className="h-full border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 flex flex-col overflow-hidden transition-colors">
                <PanelGroup orientation="horizontal" className="h-full w-full">
                  {/* Left column: Tabbed Sidebars */}
                  <Panel defaultSize={20} minSize={20} className="border-r border-gray-100 dark:border-gray-800">
                    <DataPanelTabs
                      sources={workspace.sources || []}
                      udfs={workspace.udfs || []}
                      previewDataset={previewDataset}
                      previewFunction={previewFunction}
                      setPreviewDataset={(ds) => {
                        setPreviewDataset(ds);
                        if (ds) setPreviewFunction(null);
                      }}
                      setPreviewFunction={(fn) => {
                        setPreviewFunction(fn);
                        if (fn) setPreviewDataset(null);
                      }}
                      onConnect={() => {
                        setAddItemModalType('dataset');
                        setIsAddItemModalOpen(true);
                      }}
                      onRemoveSource={handleRemoveSource}
                      onRebuildSource={handleRebuildSource}
                      onManageFunctions={() => {
                        setAddItemModalType('function');
                        setIsAddItemModalOpen(true);
                      }}
                      onRemoveFunction={(fid) => {
                        setTargetFunctionToRemove(fid);
                        setIsRemoveFunctionModalOpen(true);
                      }}
                    />
                  </Panel>

                  <Separator orientation="horizontal" />

                  {/* Right column: Unified Preview */}
                  <Panel defaultSize={80}>
                    {previewFunction ? (
                      <FunctionPreview function={previewFunction} />
                    ) : (
                      <DatasetExplorer
                        key={`${previewDataset?.id}-${previewMetadataVersion}`}
                        dataset={previewDataset}
                        onMaximize={() => setIsFullScreenPreviewOpen(true)}
                      />
                    )}
                  </Panel>
                </PanelGroup>
              </div>
            </Panel>
          )}
        </PanelGroup>
      </div>
    </div>
  );
};

const Separator = ({ orientation }) => (
  <PanelResizeHandle className={cn(
    orientation === 'horizontal' ? "w-1" : "h-1",
    "bg-gray-100 dark:bg-gray-800 hover:bg-emerald-500 transition-colors relative group shrink-0 outline-none"
  )}>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-400 group-hover:text-emerald-600 transition-colors shadow-sm z-10">
      {orientation === 'horizontal' ? <GripVertical size={12} /> : <GripHorizontal size={12} />}
    </div>
  </PanelResizeHandle>
);

export default WorkspacePage;
